import React, { useCallback, useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/src/components/ui/button";

import {
  getProjectPageHref,
  projectPageColumns,
} from "./tableParams/projectPageColumns";
import ProjectAPI from "../../api/ProjectAPI";
import DataTable from "../../components/dataTable/DataTable";
import AlertDialog from "../../components/dialog/AlertDialog";

import type { JSX } from "react";

import type { PageRankVariables } from "./ProjectPageListTable.types";
import type {
  GetProjectPageListResponse,
  ProjectPage,
} from "../../api/ProjectAPI.types";
import type { PlatformRoutesParams } from "../../components/routes/PlatformRoutes";

function sortByRank(pages: ProjectPage[]): ProjectPage[] {
  return [...pages].sort(
    (firstPage, secondPage) => firstPage.rank - secondPage.rank,
  );
}

export default function ProjectPageListTable(): JSX.Element {
  const { projectSlug } = useParams<PlatformRoutesParams>();

  const history = useHistory();

  const [defaultPageId, setDefaultPageId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const projectPageListQuery = ProjectAPI.generateQuery({
    fn: ProjectAPI.getProjectPageList,
    params: [projectSlug],
  });

  const {
    data: pageListData,
    isLoading,
    isError,
    refetch,
  } = useQuery(projectPageListQuery);

  const pageList = useMemo(
    (): ProjectPage[] => sortByRank(pageListData?.data ?? []),
    [pageListData?.data],
  );

  const { t } = useTranslation("editor-platform");

  const pageDeleteMutation = useMutation({
    mutationFn: async (pageId: string) =>
      ProjectAPI.deleteProjectPage({ pageId, projectSlug }),
    onError: () => {
      toast.error(t("error:genericError"));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(projectPageListQuery.queryKey);
    },
  });

  const pageRankMutation = useMutation({
    mutationFn: async ({ pageId, rankChange }: PageRankVariables) =>
      rankChange < 0
        ? ProjectAPI.projectPageRankUp({
            pageId,
            projectSlug,
          })
        : ProjectAPI.projectPageRankDown({
            pageId,
            projectSlug,
          }),
    onMutate: ({ pageId, rankChange }) => {
      const previousPageList = sortByRank(
        queryClient.getQueryData<GetProjectPageListResponse>(
          projectPageListQuery.queryKey,
        )?.data ?? [],
      );

      if (previousPageList.length === 0) return { previousData: undefined };

      const currentPage = previousPageList.find((page) => page.uuid === pageId);

      if (!currentPage) return { previousData: previousPageList };

      const newRank = currentPage.rank + rankChange;
      const maxRank = previousPageList.length - 1;

      if (newRank < 0 || newRank > maxRank)
        return { previousData: previousPageList };

      const pageToUpdateList: ProjectPage[] = previousPageList.reduce<
        ProjectPage[]
      >((changedPageList, page) => {
        if (page.uuid === pageId) {
          changedPageList.push({ ...page, rank: newRank });
        } else if (
          rankChange > 0 &&
          page.rank > currentPage.rank &&
          page.rank <= newRank
        ) {
          changedPageList.push({ ...page, rank: page.rank - 1 });
        } else if (
          rankChange < 0 &&
          page.rank >= newRank &&
          page.rank < currentPage.rank
        ) {
          changedPageList.push({ ...page, rank: page.rank + 1 });
        }
        return changedPageList;
      }, []);

      queryClient.setQueryData<GetProjectPageListResponse>(
        projectPageListQuery.queryKey,
        (oldData) => {
          if (!oldData) return undefined;

          const updatedPageList = oldData.data.map((page) => {
            const updatedPage = pageToUpdateList.find(
              (pageWithNewRank) => pageWithNewRank.uuid === page.uuid,
            );
            return updatedPage ?? page;
          });

          return { ...oldData, data: sortByRank(updatedPageList) };
        },
      );
      return { previousData: previousPageList };
    },
    onSuccess: () => {
      toast.success(t("pages.rankUpdateSuccess"));
    },
    onError: () => {
      toast.error(t("pages.rankUpdateError"));
      queryClient.invalidateQueries(projectPageListQuery.queryKey);
    },
  });

  const { mutate: changePageRank } = pageRankMutation;
  const isRankLoading = pageRankMutation.isLoading;

  const setDefaultHomepageMutation = useMutation({
    mutationFn: ProjectAPI.setDefaultHomepage,
    onMutate: ({ pageId }) => {
      const previousData = queryClient.getQueryData<GetProjectPageListResponse>(
        projectPageListQuery.queryKey,
      );

      if (previousData) {
        const optimisticData = {
          ...previousData,
          data: previousData.data.map((page) =>
            page.uuid === pageId
              ? { ...page, default: true }
              : { ...page, default: false },
          ),
        };

        queryClient.setQueryData(projectPageListQuery.queryKey, optimisticData);
      }

      return { previousData };
    },
    onSuccess: () => {
      toast.success(t("saveSuccess"));
    },
    onError: (_, __, context) => {
      toast.error(t("error:genericError"));

      if (context?.previousData) {
        queryClient.setQueryData(
          projectPageListQuery.queryKey,
          context.previousData,
        );
      }
    },
  });

  const { mutate: setDefaultHomepage } = setDefaultHomepageMutation;
  const { mutate: deletePage } = pageDeleteMutation;

  const handleConfirmDefault = (): void => {
    if (defaultPageId)
      setDefaultHomepage({ pageId: defaultPageId, projectSlug });
    setDefaultPageId(null);
  };

  const columnList = useMemo(
    () =>
      projectPageColumns({
        t,
        projectSlug,
        pageCount: pageList.length,
        isRankLoading,
        onRankChange: (page, rankChange) =>
          changePageRank({ pageId: page.uuid, rankChange }),
        onSetDefault: (page) => setDefaultPageId(page.uuid),
        onEdit: (page) =>
          history.push(getProjectPageHref(projectSlug, page.uuid)),
        onDelete: (page) => deletePage(page.uuid),
      }),
    [
      t,
      projectSlug,
      pageList.length,
      isRankLoading,
      changePageRank,
      deletePage,
      history,
    ],
  );

  const getRowHref = useCallback(
    (page: ProjectPage): string => getProjectPageHref(projectSlug, page.uuid),
    [projectSlug],
  );

  const defaultPageName = pageList.find(
    (page) => page.uuid === defaultPageId,
  )?.label;

  const handleAddPage = (): void => {
    history.push(getProjectPageHref(projectSlug, "new"), {
      newPageRank: pageListData?.data.length,
    });
  };

  const handleOpenChange = (isOpen: boolean): void => {
    if (!isOpen) setDefaultPageId(null);
  };

  const addButton = (
    <Button onClick={handleAddPage} size="lg" type="button">
      <Plus />
      {t("pages.add")}
    </Button>
  );

  return (
    <React.Fragment>
      <DataTable
        compactPagination
        columns={columnList}
        data={pageList}
        getRowHref={getRowHref}
        headerActions={addButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("title.pages")}
      />
      <AlertDialog
        cancelLabel={t("table:dataTable.cancel")}
        confirmLabel={t("pages.replace")}
        isOpen={defaultPageId !== null}
        onConfirm={handleConfirmDefault}
        onOpenChange={handleOpenChange}
        title={t("pages.changeHomepage")}
        description={t("pages.changeHomepageText", {
          pageName: defaultPageName,
        })}
      />
    </React.Fragment>
  );
}
